import styled, { css } from 'styled-components';
import type CSS from 'csstype';
import NextImage from 'next/image';
import campaignHeading from 'Common/styles/campaignHeading';

import { BreakpointNames } from 'Theme/types/breakpoints.type';

import Button from 'Elements/Button';
import ArrowButton from 'Elements/Button/Arrow';

import Chunky from 'Elements/Button/Arrow/Chunky';

export const JourneyHeading = styled.h2`
    ${({ theme }) => css`
        ${campaignHeading()};
        color: ${theme.colors.primary.hex};
        font-size: 1.5rem;

        @media ${theme.breakpoints.large.media} {
            font-size: 2.25rem;
        }
    `}
`;

export const JourneyChoiceWrapper = styled.div`
    ${({ theme }) => css`
        max-width: 96.5rem;
        margin: 0 1rem;
        padding: 1rem 0 2rem;

        @media ${theme.breakpoints.large.media} {
            margin: 0 2rem;
        }

        @media only screen and (min-width: 100.5rem) {
            margin: 0 auto;
        }
    `}
`;

export const CtaContainer = styled.div`
    ${({ theme }) => css`
        display: flex;
        flex-direction: column;
        gap: 1rem;

        @media ${theme.breakpoints.large.media} {
            padding: 0 2rem;
            gap: 2.25rem;
        }
    `}
`;

export const CtaChunkyButton = styled(Chunky)`
    ${({ theme }) => css`
        font-size: 1.125rem;

        @media ${theme.breakpoints.large.media} {
            font-size: 1.5rem;
        }
    `}
`;

export const JourneyButton = styled(Button)<{ $active: boolean }>`
    ${({ theme, $active }) => css`
        position: relative;
        width: 22rem;
        max-width: 100%;
        justify-content: center;
        border-radius: 0.5rem;
        color: ${theme.colors.campaigns.clearing.secondary.hex};
        background-color: ${theme.colors.campaigns.clearing.secondaryContrast.hex};
        border: ${theme.commonStyles.brandPixel} solid
            ${theme.colors.campaigns.clearing.secondary.hex};
        white-space: normal;

        ${!$active &&
        css`
            &:hover {
                color: ${theme.colors.campaigns.clearing.primary.hex};
                background-color: ${theme.colors.campaigns.clearing.secondaryContrast.hex};
                border: ${theme.commonStyles.brandPixel} solid
                    ${theme.colors.campaigns.clearing.primary.hex};
            }
        `}

        ${$active &&
        css`
            color: ${theme.colors.campaigns.clearing.primaryContrast.hex};
            background-color: ${theme.colors.campaigns.clearing.primary.hex};
            border-color: ${theme.colors.campaigns.clearing.primary.hex};
            pointer-events: none;

            &:hover {
                background-color: ${theme.colors.campaigns.clearing.primary.hex};
            }
        `}

        @media ${theme.breakpoints.medium.media} {
            height: auto;
        }

        @media ${theme.breakpoints.large.media} {
            width: 28rem;
        }
    `}
`;

export const FlowWrapper = styled.div`
    ${({ theme }) => css`
        position: relative;
        color: ${theme.colors.campaigns.clearing.secondary.hex};
    `}
`;

export const Spacer = styled.div<{
    $marginTop: Partial<Record<BreakpointNames, CSS.Property.MarginTop>>;
}>`
    ${({ theme, $marginTop }) => css`
        ${Object.keys($marginTop).map(
            key => css`
                /* Quad specificty to override Grid > first-child property */
                &&&& {
                    @media ${theme.breakpoints[key].media} {
                        margin-top: ${$marginTop[key]};
                    }
                }
            `
        )}
    `}
`;

export const StyledImage = styled(NextImage)`
    display: block;
    height: auto;
    width: 100%;
    max-width: 100%;
    margin: 0;
    border-radius: 0.5rem;
`;

export const JourneyButtonProgressLine = styled.span<{ $active: boolean }>`
    ${({ theme, $active }) => css`
        position: absolute;
        bottom: -2.5rem;
        left: 50%;
        width: 0.275rem;
        height: 2.5rem;
        background: ${$active
            ? theme.colors.campaigns.clearing.primary.hex
            : theme.colors.secondary.tints[1].hex};
        z-index: -1;
    `};
`;

const ProgressHorizontalLineCss = (theme, $active) => css`
    position: absolute;
    bottom: -2.5rem;
    width: calc(50% + 0.5rem);
    height: 0.275rem;
    background: ${$active
        ? theme.colors.campaigns.clearing.primary.hex
        : theme.colors.secondary.tints[1].hex};

    @media ${theme.breakpoints.medium.media} {
        width: calc(50% + 2.5rem);
    }
`;

export const JourneyButtonprogressLineHorizontalLeft = styled.span<{ $active: boolean }>`
    ${({ theme, $active }) => css`
        ${ProgressHorizontalLineCss(theme, $active)}
        left: 50%;
    `};
`;

export const JourneyButtonProgressLineHorizontalRight = styled.span<{ $active: boolean }>`
    ${({ theme, $active }) => css`
        ${ProgressHorizontalLineCss(theme, $active)}
        right: 50%;
    `};
`;

export const JourneyButtonProgressLineCentral = styled.span`
    ${({ theme }) => css`
        position: absolute;
        bottom: -5rem;
        width: 0.275rem;
        height: 2.5rem;
        background: ${theme.colors.campaigns.clearing.primary.hex};
        opacity: 1;

        @media ${theme.breakpoints.large.media} {
            opacity: 0;
        }
    `};
`;

export const StyledArrowButton = styled(ArrowButton)`
    ${({ theme }) => css`
        border-radius: 0.5rem !important;
        font-size: 1.125rem;
        margin-top: 1.5rem;
        width: 100%;
        white-space: normal;

        @media ${theme.breakpoints.large.media} {
            font-size: 1.5rem;
            width: 90%;
        }
    `};
`;
